import clsx from 'clsx';
import { ReactNode, SyntheticEvent, useRef } from 'react';
import { KeyboardArrowDown } from '@material-ui/icons';
import { Button } from '../Button/NewButton';
import { AccordionHeaderAction, useAccordion } from './index';

interface AccordionHeaderProps extends BaseProps {
  actions?: AccordionHeaderAction[];
  expandIcon?: ReactNode;
  expandIconRotation?: 90 | 180 | 270;
  expandIconSide?: 'left' | 'right';
  onExpand?(): void;
}

export const AccordionHeader = ({
  actions,
  children,
  className,
  expandIcon = <KeyboardArrowDown />,
  expandIconRotation = 180,
  expandIconSide = 'right',
  onExpand,
  ...rest
}: AccordionHeaderProps) => {
  const { expanded, handleExpand } = useAccordion();

  const ref = useRef<HTMLElement>(null);
  const classes = useStyles();

  const handleActionClick = (
    event: SyntheticEvent<HTMLButtonElement>,
    action: AccordionHeaderAction
  ) => {
    if (!action.expandOnClick) {
      event.stopPropagation();
      event.preventDefault();
    }

    action.action();
  };

  const renderActions = () => {
    if (actions === undefined) return;

    return (
      <div className="inline-flex items-center gap-4 ml-auto text-sm font-normal">
        {actions.map((action, index) => (
          <Button
            key={`accordion-action-${index + 1}`}
            variant="text"
            onClick={(event: SyntheticEvent<HTMLButtonElement>) =>
              handleActionClick(event, action)
            }
          >
            {action.name}
          </Button>
        ))}
      </div>
    );
  };

  const renderExpandIcon = () => {
    if (!expandIcon) return null;

    return (
      <div
        className={clsx('transition duration-300', {
          [classes.iconRotation[expandIconRotation]]: expanded,
          ['ml-4 mr-2']: expandIconSide === 'left',
          ['ml-2 mr-4']: expandIconSide === 'right',
        })}
      >
        {expandIcon}
      </div>
    );
  };

  return (
    <summary
      className={clsx(
        className,
        'flex flex-grow items-center list-none h-16 cursor-pointer',
        {
          ['flex-row-reverse']: expandIconSide === 'right',
        }
      )}
      ref={ref}
      onClick={(event) => {
        event.preventDefault();
        handleExpand(!expanded);
        if (onExpand) onExpand();
      }}
      {...rest}
    >
      {renderExpandIcon()}
      <div
        className={clsx('w-full flex space-between items-center', {
          ['m-4']: !expandIcon,
          ['ml-2 mr-4']: expandIcon && expandIconSide === 'left',
          ['ml-4 mr-2']: expandIcon && expandIconSide === 'right',
        })}
      >
        {children}
        {renderActions()}
      </div>
    </summary>
  );
};

const useStyles = () => ({
  iconRotation: {
    90: 'rotate-90',
    180: 'rotate-180',
    270: 'rotate-[270deg]',
  },
});
